import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import useIntl from '@/utils/intl'
import { Button, MenuItem, TextField } from '@mui/material'
import React, { FunctionComponent, useReducer } from 'react'
import { ContactsV2 } from '@/services/mobi-api/numberv2/types'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import { Obj } from '..'
import { onlyCyrill, phoneRegex2 } from '@/utils/constants'
import { useLanguage } from '@/context/LanguageContext'

const contacts = [
  {
    en: 'Father',
    mn: 'Эцэг'
  },
  {
    en: 'Mother',
    mn: 'Эх'
  },
  {
    en: 'Brother',
    mn: 'Ах'
  },
  {
    en: 'Sister',
    mn: 'Эгч'
  },
  {
    en: 'Little brother/sister',
    mn: 'Дүү'
  },
  {
    en: 'A friend',
    mn: 'Найз'
  },
  {
    en: 'Others',
    mn: 'Бусад'
  }
]

interface State {
  name?: string
  who?: string
  phonenumber?: string
  name2?: string
  who2?: string
  phonenumber2?: string
  loading?: boolean
}

interface Props {
  next: (obj: Obj) => void
  back: () => void
  onValidationChange: (valid: boolean) => void
  instanceId: string
}

const ContactInfo: FunctionComponent<Props> = ({
  next,
  back,
  instanceId,
  onValidationChange
}) => {
  const { translate } = useIntl()
  const { numberv2 } = useMobiApi()
  const { showError } = useToast()
  const { locale } = useLanguage()
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      who: '',
      who2: ''
    }
  )
  const handleChange = (name: string, value: string): void => {
    setState({ [name]: value })
  }

  React.useEffect(() => {
    const isValid =
      !!state.name &&
      !!state.who &&
      !!state.phonenumber &&
      state.phonenumber.length === 8
    onValidationChange(isValid)
  }, [state.name, state.who, state.phonenumber])

  const handleSaveContactInfo = async (): Promise<void> => {
    if (
      (state.name ?? '').trim() === '' ||
      !onlyCyrill.test(state.name ?? '')
    ) {
      showError(translate('error.cyrillError'))
    } else if (
      (state.phonenumber ?? '').trim() === '' ||
      !phoneRegex2.test(state.phonenumber ?? '')
    ) {
      showError(translate('error.isdnError'))
    } else {
      try {
        const contact1: ContactsV2 = {
          type: 'contact1',
          name: state.name ?? '',
          who: state.who ?? '',
          phoneNumber: state.phonenumber ?? ''
        }

        const contact2: ContactsV2 = {
          type: 'contact2',
          name: state.name2 ?? '',
          who: state.who2 ?? '',
          phoneNumber: state.phonenumber2 ?? ''
        }

        const req: ContactsV2[] = []

        if (
          contact1.name !== '' &&
          contact1.who !== '' &&
          contact1.phoneNumber !== ''
        ) {
          req.push(contact1)
        }

        if (
          contact2.name !== '' &&
          contact2.who !== '' &&
          contact2.phoneNumber !== ''
        ) {
          req.push(contact2)
        }

        const res = await numberv2.saveContactV2({ instanceId, contacts: req })

        if (res?.error !== undefined) {
          showError(
            locale === 'en'
              ? res?.cause?.messageEn ??
                  res?.error ??
                  translate('request.failed')
              : res?.cause?.messageMn ??
                  res?.error ??
                  translate('request.failed')
          )
        } else {
          next({ contactInfo: req, danUrl: res?.danRedirectUrl })
        }
      } catch (e) {
        showError(translate('request.failed'))
      } finally {
        setState({ loading: false })
      }
    }
  }

  return (
    <div className='bg-surfaceMid rounded-3xl p-6 md:p-8 flex flex-col items-start justify-center gap-4'>
      {/* <DarkButton onClick={back}>back</DarkButton> */}
      <div className='flex flex-col gap-3 w-full'>
        <span className='p-3 text-sm font-bold text-textHigh'>
          {translate('stars.contact')} - 1{' '}
          <span className='text-xs text-textMid font-bold'>
            ({translate('required')})
          </span>
        </span>
        <TextField
          label={translate('order.delivery.firstName')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          name='name'
          onChange={(e) => {
            if (e.target.value.length < 64) {
              handleChange(e.target.name, e.target.value)
            }
          }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
        <TextField
          label={translate('relation')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          name='who'
          value={state.who !== undefined ? state.who : ''}
          onChange={(e) => {
            if (e.target.value.length < 64) {
              handleChange(e.target.name, e.target.value)
            }
          }}
          select
          SelectProps={{
            MenuProps: {
              sx: { maxHeight: '40%' }
            }
          }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        >
          {contacts.map((item, id) => (
            <MenuItem
              key={id}
              value={item.mn}
              className='bg-surfaceInverse text-textHigh'
            >
              {locale === 'en' ? item.en : item.mn}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={translate('stars.contact')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          type='number'
          name='phonenumber'
          onChange={(e) => {
            handleChange(e.target.name, e.target.value)
          }}
          inputProps={{ inputMode: 'numeric' }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
      </div>
      <div className='flex flex-col gap-3 w-full'>
        <span className='p-3 text-sm font-bold text-textHigh'>
          {translate('stars.contact')} - 2
        </span>
        <TextField
          label={translate('order.delivery.firstName')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          name='name2'
          onChange={(e) => {
            if (e.target.value.length < 64) {
              handleChange(e.target.name, e.target.value)
            }
          }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
        <TextField
          label={translate('relation')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          name='who2'
          value={state.who2 !== undefined ? state.who2 : ''}
          onChange={(e) => {
            if (e.target.value.length < 64) {
              handleChange(e.target.name, e.target.value)
            }
          }}
          select
          SelectProps={{
            MenuProps: {
              sx: { maxHeight: '40%' }
            }
          }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        >
          {contacts.map((item, id) => (
            <MenuItem
              key={id}
              value={item.mn}
              className='bg-surfaceInverse text-textHigh'
            >
              {locale === 'en' ? item.en : item.mn}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={translate('stars.contact')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          type='number'
          name='phonenumber2'
          onChange={(e) => {
            handleChange(e.target.name, e.target.value)
          }}
          inputProps={{ inputMode: 'numeric' }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
      </div>
      <Button
        id='contact-continue'
        style={{ display: 'none' }}
        onClick={() => {
          void handleSaveContactInfo()
        }}
      />
      {/* <DarkButton fullwidth disabled={state.name === '' || state.who === '' || state.phonenumber === '' || state.phonenumber?.length !== 8} onClick={() => { void handleSaveContactInfo() }}>{translate('continue')}</DarkButton> */}
    </div>
  )
}
export default ContactInfo
